import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { PlanningVersionsPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import Id from 'modules/time_planning/components/Id'
import { usePlanningVersions } from 'modules/time_planning/store/collections/planningVersions'
import type PlanningVersion from 'modules/time_planning/store/models/PlanningVersion'

interface Props {
  employeeId: string
  canEditPlanningVersions: boolean
  canSeePlanningVersions: boolean
  canEditShifts: boolean
  canEditContracts: boolean
}

const IndexReal = observer(
  ({
    employeeId,
    canSeePlanningVersions,
    canEditPlanningVersions,
    canEditShifts,
    canEditContracts,
  }: Props) => {
    const { navigation } = useNavigation()

    const { planningVersion } = useOutletContext<{
      planningVersion: PlanningVersion | null | undefined
    }>()

    const navigate = useNavigate()

    if (!canSeePlanningVersions) navigate(navigation.employees.id.fullPath({ id: employeeId }))

    const planningVersions = usePlanningVersions()

    const getPlanningVersion = () => {
      if (planningVersion) return planningVersion

      return planningVersions.currentEmployeePlanningVersionWithinDate(moment(), employeeId)
    }

    const selectedPlanningVersion = getPlanningVersion()

    const { data, loading: loadingPolicies } = useQuery(PlanningVersionsPageDocument)

    const canAssignEmployees =
      data?.workSchedule.canAssignEmployeesPoliciesConnection.nodes[0]?.can ?? false
    const canManageCompanySchedules =
      data?.workSchedule._policies.manageCompanySchedules.can ?? false

    if (loadingPolicies || !selectedPlanningVersion) {
      return (
        <Box alignItems="center" width="full" padding="s32">
          <Spinner />
        </Box>
      )
    }

    return (
      <Id
        planningVersion={selectedPlanningVersion}
        canEditPlanningVersions={canEditPlanningVersions}
        canEditShifts={canEditShifts}
        canEditContracts={canEditContracts}
        employeeId={employeeId}
        canAssignEmployees={canAssignEmployees}
        canManageCompanySchedules={canManageCompanySchedules}
      />
    )
  }
)

const PlanningVersionsPage = ({
  employeeId,
  canSeePlanningVersions,
  canEditPlanningVersions,
  canEditShifts,
  canEditContracts,
}: Props) => (
  <IndexReal
    employeeId={employeeId}
    canSeePlanningVersions={canSeePlanningVersions}
    canEditPlanningVersions={canEditPlanningVersions}
    canEditShifts={canEditShifts}
    canEditContracts={canEditContracts}
  />
)

export default PlanningVersionsPage
